import { Controller } from "@hotwired/stimulus"

// Handles the inline ping button on each LLM provider row.
//
// On click it POSTs to the provider's ping endpoint, then updates:
//   - statusBadge  — inline ping result (OK / Error / spinner)
//   - lastPingedAt — "X seconds ago" timestamp
//   - pingButton   — disabled + loading state while in-flight
//
// On completion it dispatches a `notification:show` window event so the
// global NotificationComponent can display the outcome as a toast.
export default class extends Controller {
  static targets = ["pingButton", "pingIcon", "pingLabel", "statusBadge", "lastPingedAt"]
  static values  = { pingUrl: String }

  async ping() {
    this.#setLoading(true)

    try {
      const response = await fetch(this.pingUrlValue, {
        method:  "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          "Accept":       "application/json"
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const data = await response.json()
      this.#updateStatusBadge(data)
      this.#updateLastPingedAt(data.last_pinged_at)
      this.#notify(data)
    } catch (error) {
      this.#renderErrorBadge(error.message)
      window.dispatchEvent(new CustomEvent("notification:show", {
        detail: { title: "Ping failed", message: error.message, type: "error" }
      }))
    } finally {
      this.#setLoading(false)
    }
  }

  // ── private ──────────────────────────────────────────────────────────────

  #setLoading(loading) {
    if (this.hasPingButtonTarget) {
      this.pingButtonTarget.disabled = loading
    }

    if (this.hasPingLabelTarget) {
      this.pingLabelTarget.textContent = loading ? "Pinging…" : "Ping"
    }

    if (this.hasPingIconTarget) {
      this.pingIconTarget.classList.toggle("animate-spin", loading)
    }
  }

  #updateStatusBadge(data) {
    if (!this.hasStatusBadgeTarget) return

    if (data.ok) {
      this.statusBadgeTarget.innerHTML = `
        <span class="inline-flex items-center gap-x-1 text-green-700 font-medium">
          <svg class="size-3.5 shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fill-rule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z" clip-rule="evenodd" />
          </svg>
          OK
        </span>
      `
    } else {
      this.#renderErrorBadge(data.error_message || "Error")
    }
  }

  #renderErrorBadge(message) {
    if (!this.hasStatusBadgeTarget) return

    this.statusBadgeTarget.innerHTML = `
      <span class="inline-flex items-center gap-x-1 text-red-600 font-medium" title="${this.#escapeHtml(message)}">
        <svg class="size-3.5 shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fill-rule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM8.28 7.22a.75.75 0 0 0-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 1 0 1.06 1.06L10 11.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L11.06 10l1.72-1.72a.75.75 0 0 0-1.06-1.06L10 8.94 8.28 7.22Z" clip-rule="evenodd" />
        </svg>
        Error
      </span>
    `
  }

  #updateLastPingedAt(isoTimestamp) {
    if (!this.hasLastPingedAtTarget || !isoTimestamp) return

    const date = new Date(isoTimestamp)
    const seconds = Math.round((Date.now() - date.getTime()) / 1000)
    const label = seconds < 5 ? "just now" : `${seconds}s ago`

    this.lastPingedAtTarget.innerHTML = `&bull; <time datetime="${isoTimestamp}">${label}</time>`
  }

  #notify(data) {
    if (data.ok) {
      const modelCount = Array.isArray(data.models) ? data.models.length : 0
      const modelText  = modelCount === 1 ? "1 model" : `${modelCount} models`
      window.dispatchEvent(new CustomEvent("notification:show", {
        detail: {
          title:   "Ping successful",
          message: `Provider responded in ${data.duration_ms}ms — ${modelText} available.`,
          type:    "success"
        }
      }))
    } else {
      window.dispatchEvent(new CustomEvent("notification:show", {
        detail: {
          title:   "Ping failed",
          message: data.error_message || "Provider did not respond.",
          type:    "error"
        }
      }))
    }
  }

  #escapeHtml(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
  }
}
